import { CharacterInputValue } from '@tencent/tds-ui';
import { Button, message, Table, Typography } from 'antd';
import { isEmpty } from 'lodash';
import React from 'react';
import { logger } from '@/infrastructure/logger';
import { MATCH_MODE_TEXT, MatchMode } from '@/presentation/components/form/standards/types';
import { FIELD_ALL } from '@/proto/field';
import { TdsDateCascaderValue } from '../../tds-form/tds-date-cascader';


export interface CompareListColumns {
  title: string;
  dataIndex: string;
  disabled?: boolean;
  render?: (value: any) => React.ReactNode;
}

type IProps<T> = {
  columns: CompareListColumns[];
  dataSource: T[];
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (selectedRowKeys: React.Key[]) => void;
  onDelete: (id: string, index: number) => void;
};

export type FieldTyp = {mode: string, values: string[]};

export const renderField = (version: FieldTyp, options?: any[]) => {
  if (!version) {
    return undefined;
  }
  if (Array.isArray(version)) {
    return version?.join(',');
  }
  const operator = options?.find?.(item => item.value === version?.mode)?.label;

  const values = version?.values?.includes?.(FIELD_ALL)
  || version?.values?.length === 0 || !version ? '所有' : version?.values;

  return `${operator ? `(${operator})` : ''} ${values || '所有'}`;
};

export const renderStanderdTimeRange = (timeRange: TdsDateCascaderValue) => {
  if (!timeRange) {
    return undefined;
  }

  return `${timeRange?.date_value?.[0]}~${timeRange?.date_value?.[1]}`;
};

export const renderStandardVersionLabel = (version: CharacterInputValue) => {
  if (!version) {
    return undefined;
  }

  let valueLabel = '';
  const operator = MATCH_MODE_TEXT[version?.mode as MatchMode];

  if ([MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(version?.mode as MatchMode)) {
    valueLabel = '';
  } else if ([MatchMode.Between].includes(version?.mode as MatchMode)) {
    valueLabel = isEmpty(version?.values) ? '' : `[${version?.values?.min}, ${version?.values?.max}]`;
  } else {
    valueLabel = isEmpty(version?.values) ? '所有' : version.values;
  }

  return `(${operator}) ${valueLabel}`;
};

export const renderCharacterInputValue = (fieldValue: CharacterInputValue & {operator?: string}) => {
  if (!fieldValue) {
    return '';
  }
  if (Array.isArray(fieldValue)) {
    return fieldValue?.join?.(',');
  }
  const operator = MATCH_MODE_TEXT[fieldValue?.mode || fieldValue?.operator || ''] || '';
  const values = fieldValue?.values?.includes?.(FIELD_ALL) || !fieldValue?.values?.length ? '所有' : fieldValue?.values;

  return `(${operator}) ${values}`;
};

export const CompareList = <T extends Object>(props: IProps<T>) => {
  const { columns, dataSource, selectedRowKeys, setSelectedRowKeys, onDelete } = props;

  const renderDefault = (value: any) => {
    if (typeof value === 'string') {
      return value === FIELD_ALL ? '所有' : value;
    }
    if (Array.isArray(value)) {
      return value.includes(FIELD_ALL) ? '所有' : value.join('~');
    }
    return value;
  };

  const renderIndex = (_text: string, _record: any, index: number) => (index < 9 ? `0${index + 1}` : `${index + 1}`);
  const renderButton = (_text: string, record: any, index: number) => <Button
    type={'link'}
    style={{ padding: 0 }}
    onClick={() => {
      onDelete(record, index);
    }}>
    删除
  </Button>;

  const formatColumns = [
    { title: <Typography.Text strong>序号</Typography.Text>, dataIndex: 'id', render: renderIndex },
    ...(columns.filter(column => !column.disabled)
      .map(column => ({
        title: <Typography.Text strong>{column.title}</Typography.Text>,
        dataIndex: column.dataIndex,
        render: column.render ?? renderDefault,
      }))),
    { title: <Typography.Text strong>操作</Typography.Text>, dataIndex: 'action', render: renderButton },
  ];

  return <Table
    rowSelection={{
      selectedRowKeys,
      onChange: (selectedRowKeys) => {
        logger.debug('selectedRowKeys', selectedRowKeys);
        if (selectedRowKeys.length > 5) {
          message.warning('最多选可择五个');
        } else {
          setSelectedRowKeys(selectedRowKeys);
        }
      },
    }}
    rowKey={'id'}
    columns={formatColumns}
    pagination={false}
    dataSource={dataSource}
  />;
};
